/**
 * useBillingPaymentMethods — catalogue-driven payment-method availability.
 *
 * Thin composition seam over the v2 instruction options: `useInstructionOptions`
 * (one shared React Query cache entry, ws/session/body resolved internally) ×
 * the pure `billing.helpers` ladder. No store writes, no legacy provider —
 * MODULE-CONTRACT §11 Kind-A ("MAY reuse React Query data hooks").
 *
 * Deliberately shared by TWO call sites, which is why it is a hook and not
 * inlined in the page:
 *   - `Billing.page.tsx` — renders the cards + the render-time preselect,
 *   - `components/cart/useStepNav.ts` — owns the ACTUAL Billing "Next" gate
 *     (`selectBillingStepValidFor`). Both must read the same default or the
 *     preselect would look enabled and gate closed.
 *
 * Costs nothing extra on other steps: `useStepNav` already mounts
 * `useResolvedStepFlow`, which mounts the same instruction-options query.
 */

import { useMemo } from 'react'

import type { PaymentMethod } from 'models/purchaseFlowRevamp'
import type { ProductKind } from 'constants/product'
import { bucketInstructionOptions, useInstructionOptions } from 'dataQueries/purchaseFlowRevampInstructions.query'

import { useAuth0 } from 'utils/auth'
import { useUserData } from 'components/contexts/UserDataContext'
import { usePfStore } from '../../store'
import type { PaymentMethodOffer } from './billing.helpers'
import {
  resolveAvailablePaymentMethods,
  resolveCardPaymentKind,
  resolveDefaultPaymentMethod,
  paymentMethodForPreferredKind,
  resolvePaymentMethodOffers,
  resolvePreselectedMethod
} from './billing.helpers'

export interface BillingPaymentMethods {
  /** Methods to render, per the §6.2 availability ladder. */
  methods: ReadonlyArray<PaymentMethod>
  /** BE-preferred method — a RENDER-TIME effective value only (§11.1: never
   *  seeded into the persisted slice). `undefined` until the options are ready. */
  defaultPaymentMethod?: PaymentMethod
  /** Actual wire kind (`CARD_PAYMENT` | `STRIPE_PAYMENT`) behind the `'card'`
   *  bucket for THIS session, so callers building the wire body can echo it
   *  back instead of assuming Stripe (R-3 CLOSED, ledger #14). `undefined`
   *  until the options are ready, or when the org offers no card-family option. */
  cardPaymentKind?: ProductKind
  /** True while the options are in flight — the section shows a spinner instead
   *  of flashing the fallback three. */
  isLoading: boolean
  /** The options arrived or failed; before that the offers are the fallback three. */
  isSettled: boolean
  /** Per-method option + price + permission, for the card rendering. */
  offers: ReadonlyArray<PaymentMethodOffer>
  /** The options call failed — the step falls open on the hardcoded three, so
   *  say so and offer a retry rather than presenting guesses as fact. */
  hasError: boolean
  /** Re-runs the options request. */
  refetch: () => void
}

/**
 * @example
 * const { methods, defaultPaymentMethod, isLoading } = useBillingPaymentMethods()
 * const effective = paymentMethod ?? defaultPaymentMethod
 */
export const useBillingPaymentMethods = (): BillingPaymentMethods => {
  const { data, status, refetch } = useInstructionOptions()
  const { roles } = useAuth0()
  const { userCanPayByInvoice, clientData } = useUserData()
  // On-behalf orders are judged on the CLIENT's permission, captured by the
  // admin gate — the admin's own `userCanPayByInvoice` describes the wrong
  // party (same reasoning as DF-I7's entitlement split).
  const targetUserId = usePfStore((s) => s.targetUserId)
  const targetInvoiceAllowed = usePfStore((s) => s.targetInvoiceAllowed)

  const billingOptions = useMemo(() => bucketInstructionOptions(data).billingOptions, [data])

  const methods = useMemo(() => resolveAvailablePaymentMethods(billingOptions, status), [billingOptions, status])

  const defaultPaymentMethod = useMemo(
    () => resolveDefaultPaymentMethod(billingOptions, status),
    [billingOptions, status]
  )

  const cardPaymentKind = useMemo(
    () => resolveCardPaymentKind(billingOptions, status),
    [billingOptions, status]
  )

  const permissions = useMemo(
    () => ({
      isAdmin: !!roles.isAdmin,
      invoiceAllowed: targetUserId ? targetInvoiceAllowed === true : !!userCanPayByInvoice,
    }),
    [roles.isAdmin, targetUserId, targetInvoiceAllowed, userCanPayByInvoice]
  )

  const offers = useMemo(
    () => resolvePaymentMethodOffers(methods, billingOptions, permissions),
    [methods, billingOptions, permissions]
  )

  // Legacy's ladder: the client's own saved preference first, then the BE
  // default, and never one they may not use.
  const permittedDefault = useMemo(
    () => resolvePreselectedMethod(
      offers,
      defaultPaymentMethod,
      // An admin ordering on behalf must not impose their own preference.
      targetUserId ? undefined : paymentMethodForPreferredKind(clientData?.preferredPaymentMethod)
    ),
    [offers, defaultPaymentMethod, targetUserId, clientData?.preferredPaymentMethod]
  )

  return {
    methods,
    offers,
    defaultPaymentMethod: permittedDefault,
    cardPaymentKind,
    isLoading: status === 'loading',
    isSettled: status === 'ready' || status === 'error',
    hasError: status === 'error',
    refetch,
  }
}
